import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CheckCircle } from "lucide-react";
import { logger } from "@/lib/logger";

export const VerificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Listen to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        checkVerificationStatus(session.user.id, session.user.created_at);
      } else {
        setShowPrompt(false);
      }
    });

    // Also check on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        checkVerificationStatus(session.user.id, session.user.created_at);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkVerificationStatus = async (userId: string, createdAt: string) => {
    try {
      // Check if onboarding is completed
      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_completed, verification_prompt_dismissed_at")
        .eq("user_id", userId)
        .maybeSingle();

      // Don't show prompt if onboarding is not completed
      if (!profile?.onboarding_completed) return;

      // Check if already verified
      const { data: verification } = await supabase
        .from("verifications")
        .select("status")
        .eq("user_id", userId)
        .maybeSingle();

      if (verification?.status === "verified") return;

      // Check if user was created more than 24 hours ago
      const userCreatedAt = new Date(createdAt);
      const hoursSinceCreation = (Date.now() - userCreatedAt.getTime()) / (1000 * 60 * 60);

      // Check if user has dismissed the prompt recently (within last 24 hours)

      if (profile?.verification_prompt_dismissed_at) {
        const dismissedAt = new Date(profile.verification_prompt_dismissed_at);
        const hoursSinceDismissal = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60);
        if (hoursSinceDismissal < 24) return;
      }

      if (hoursSinceCreation >= 24) {
        setShowPrompt(true);
      }
    } catch (error) {
      logger.error("Error checking verification status:", error);
    }
  };

  const handleVerifyId = () => {
    setShowPrompt(false);
    navigate("/verification");
  };

  const handleVerifySocials = () => {
    setShowPrompt(false);
    navigate("/account-settings#trust");
  };

  const handleDismiss = async () => {
    setShowPrompt(false);
    
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase
        .from("profiles")
        .update({ verification_prompt_dismissed_at: new Date().toISOString() })
        .eq("user_id", session.user.id);
    }
  };

  return (
    <AlertDialog open={showPrompt} onOpenChange={setShowPrompt}>
      <AlertDialogContent className="bg-white">
        <AlertDialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-accent-green/10 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-accent-green" />
            </div>
          </div>
          <AlertDialogTitle className="text-center text-primary">
            Verify Your Identity
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center text-primary/70">
            Get verified to build trust with other Dwellies! Verified accounts get more connections and appear higher in search results.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2">
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <AlertDialogAction onClick={handleVerifyId} className="flex-1 bg-accent-green hover:bg-accent-green/90">
              Verify ID
            </AlertDialogAction>
            <AlertDialogAction onClick={handleVerifySocials} className="flex-1 bg-primary hover:bg-primary/90">
              Verify Socials
            </AlertDialogAction>
          </div>
          <AlertDialogCancel onClick={handleDismiss} className="w-full">
            Maybe Later
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
